import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

type BackfillRun = {
  runId: string;
  triggerType: string;
  lookbackDays: number;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  message: string | null;
};

type BackfillWindow = {
  id: number;
  runId: string;
  siteId: number;
  streamType: string;
  windowStart: string;
  windowEnd: string;
  status: string;
  retries: number;
  rawInsertCount: number;
  dataUpsertCount: number;
  validationOk: boolean | null;
  validationMessage: string | null;
  createdAt: string;
};

type BackfillError = {
  id: number;
  runId: string;
  siteId: number;
  windowStart: string;
  windowEnd: string;
  streamType: string;
  errorMessage: string;
  createdAt: string;
};

async function getJson<T>(url: string): Promise<T> {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    throw new Error(`JSON yerine farklı yanıt döndü (${res.status}). URL: ${url}. İlk içerik: ${text.slice(0, 120)}`);
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Request failed');
  return data as T;
}

export default function GridboxBackfillTab() {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<BackfillRun[]>([]);
  const [windows, setWindows] = useState<BackfillWindow[]>([]);
  const [errors, setErrors] = useState<BackfillError[]>([]);
  const [streamType, setStreamType] = useState<'endex' | 'endex_billing' | 'load_profile' | ''>('');
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [runsRes, windowsRes, errorsRes] = await Promise.all([
        getJson<{ runs: BackfillRun[] }>('/gridbox/backfill/runs?limit=20'),
        getJson<{ windows: BackfillWindow[] }>('/gridbox/backfill/windows?limit=200'),
        getJson<{ errors: BackfillError[] }>('/gridbox/backfill/errors?limit=200'),
      ]);
      setRuns(runsRes.runs || []);
      setWindows(windowsRes.windows || []);
      setErrors(errorsRes.errors || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Backfill verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15000);
    return () => clearInterval(timer);
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/gridbox/backfill/retry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          streamType: streamType || undefined,
          resetRetries: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Retry başarısız');
      toast.success(`Retry queue: ${data.queued}`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Retry başarısız');
    } finally {
      setRetrying(false);
    }
  };

  const stats = useMemo(() => {
    const total = windows.length;
    const completed = windows.filter((w) => w.status === 'completed').length;
    const failed = windows.filter((w) => w.status === 'failed').length;
    const running = windows.filter((w) => w.status === 'running').length;
    return { total, completed, failed, running };
  }, [windows]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">GridBox Backfill v2 izleme</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-xs"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
          <select
            className="rounded border border-slate-300 px-2 py-1.5 text-xs"
            value={streamType}
            onChange={(e) => setStreamType(e.target.value as '' | 'endex' | 'endex_billing' | 'load_profile')}
          >
            <option value="">Tüm streamler</option>
            <option value="endex">endex</option>
            <option value="endex_billing">endex_billing</option>
            <option value="load_profile">load_profile</option>
          </select>
          <button
            onClick={() => void handleRetry()}
            className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-1.5 text-xs text-white"
            disabled={retrying}
          >
            <RotateCcw size={14} className={retrying ? 'animate-spin' : ''} /> Failed Retry
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded border border-slate-200 bg-white p-3 text-sm">Toplam: <b>{stats.total}</b></div>
        <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">Completed: <b>{stats.completed}</b></div>
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm">Failed: <b>{stats.failed}</b></div>
        <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm">Running: <b>{stats.running}</b></div>
      </div>

      <div className="rounded border border-slate-200 bg-white p-3">
        <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Backfill Runs</div>
        <div className="max-h-48 overflow-auto text-xs">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-500">
                <th>Run</th><th>Status</th><th>Trigger</th><th>Started</th><th>Message</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.runId} className="border-t border-slate-100">
                  <td>{r.runId}</td><td>{r.status}</td><td>{r.triggerType}</td><td>{new Date(r.startedAt).toLocaleString('tr-TR')}</td><td>{r.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded border border-slate-200 bg-white p-3">
        <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Latest Errors</div>
        <div className="max-h-64 overflow-auto text-xs">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-500">
                <th>Zaman</th><th>Site</th><th>Stream</th><th>Window</th><th>Hata</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 align-top">
                  <td>{new Date(e.createdAt).toLocaleString('tr-TR')}</td>
                  <td>{e.siteId}</td>
                  <td>{e.streamType}</td>
                  <td>{e.windowStart}..{e.windowEnd}</td>
                  <td className="max-w-[520px] break-all text-red-600">{e.errorMessage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
